import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Settings,
  Sun,
  Moon,
  Power,
  Bell,
  ChevronDown,
  User,
  Clock,
  Search,
} from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { Input } from "@/components/ui/input";

export default function Navbar({
  showSearch = false,
  loginOnly = false, //
  searchQuery = "",
  onSearchChange,
  logout,
}) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isLogoutHovered, setIsLogoutHovered] = useState(false);
const LOGO_DARK = "https://techpulseinsider.com/Logos/campaigniq-white-logo.svg";
const LOGO_LIGHT = "https://techpulseinsider.com/Logos/campaigniq-black-logo.svg";

  const BG_COLOR = "#041221";
  const NEON_COLOR = "#cffa39";
  const NEON_COLOR_LIGHT = "rgba(207, 250, 57, 0.1)";
  const NEON_COLOR_MEDIUM = "rgba(207, 250, 57, 0.3)";

  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.role;
  const currentTimezone = localStorage.getItem("timezone") || "Not set";

  const getInitials = (name = "") => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const userName = user?.name || user?.fullName || user?.company || "User";
  const userEmail = user?.email || "";
  const initials = getInitials(userName);

  // Custom styles using only your brand colors
  const styles = {
    background: {
      light: "bg-white",
      dark: `bg-[${BG_COLOR}]`,
    },
    text: {
      light: "text-gray-800",
      dark: `text-[${NEON_COLOR}]`,
    },
    border: {
      light: "border-gray-200",
      dark: `border-[${NEON_COLOR}]/20`,
    },
    hover: {
      light: "hover:bg-gray-50",
      dark: `hover:bg-[${NEON_COLOR}]/5`,
    },
  };

  return (
    <div className="flex items-center justify-between mb-8">
      {/* Logo — links back to the homepage/dashboard */}
    <Link to="/" aria-label="Go to homepage">
      <img
        key={theme} // forces re-render animation
        src={theme === "dark" ? LOGO_DARK : LOGO_LIGHT}
        width="160px"
        alt="Leadify Logo"
        className="cursor-pointer transition-opacity duration-300 opacity-90 hover:opacity-100"
      />
    </Link>


      <div className="flex items-center gap-2">
        {/* 🔍 Search — ONLY when showSearch = true */}
        {showSearch && (
          <div className="relative w-[250px] mr-2 ">
            <div className="relative group">
              {/* Component-style placeholder */}
              <div
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 pointer-events-none 
                 ${searchQuery ? "opacity-0" : "opacity-100"} transition-opacity`}
              >
                <Search size={16} />
                <span className="text-sm">Search Campaigns...</span>
              </div>
              <div className="neon-input-wrap">
                <Input
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className={`
    neon-input
    w-full rounded-xl
    pl-4 pr-4 py-2.5
    bg-transparent outline-none
    
  `}
                />
              </div>
            </div>
          </div>
        )}
       

        {/* Theme toggle with brand colors */}
        <button
          onClick={toggleTheme}
          className={`p-2.5 rounded-lg transition-all duration-300 ${
            theme === "dark"
              ? "hover:bg-[#cffa39]/10 text-[#cffa39]"
              : "hover:bg-gray-100 text-gray-600"
          }`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          <div className="relative w-5 h-5">
            {theme === "dark" ? (
              <Sun
                size={20}
                className="hover:rotate-90 transition-transform duration-500"
              />
            ) : (
              <Moon
                size={20}
                className="hover:rotate-12 transition-transform duration-500"
              />
            )}
          </div>
        </button>

        {/* Settings Dropdown */}
        {!loginOnly && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  theme === "dark"
                    ? "hover:bg-[#cffa39]/10 text-[#cffa39]"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <Settings
                  size={20}
                  className="hover:rotate-90 transition-transform duration-500"
                />
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="end"
              side="bottom"
              className={`w-56 p-2 shadow-xl ${
                theme === "dark"
                  ? "bg-[#041221] border-[#cffa39]/20 text-white"
                  : "bg-white border-gray-200 text-gray-800"
              }`}
            >
              <div className="space-y-1">
                <button
                  onClick={() => navigate("/preferability")}
                  className={`w-full flex flex-col items-start justify-between px-3 py-2.5 text-sm rounded-md transition-colors ${
                    theme === "dark"
                      ? "hover:bg-[#cffa39]/10"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Clock
                      size={14}
                      className={
                        theme === "dark" ? "text-[#cffa39]" : "text-gray-600"
                      }
                    />
                    <span>Timezone</span>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      theme === "dark"
                        ? "bg-[#cffa39]/20 text-[#cffa39]"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {currentTimezone}
                  </span>
                </button>

                {role === "admin" && (
                  <button
                    onClick={() => navigate("/user-management")}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-md transition-colors ${
                      theme === "dark"
                        ? "hover:bg-[#cffa39]/10"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <User
                      size={14}
                      className={
                        theme === "dark" ? "text-[#cffa39]" : "text-gray-600"
                      }
                    />
                    User Management
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* User Profile with Branded Logout */}
        {!loginOnly && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 group">
                <div className="relative">
                  {/* User avatar with neon gradient */}
                  <div
                    className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-bold uppercase text-sm shadow-lg
                  transition-all duration-300 group-hover:scale-105
                  ${
                    theme === "dark"
                      ? "bg-gradient-to-br from-[#041221] to-[#0a1a2f] border border-[#cffa39]/30 text-[#cffa39]"
                      : "bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 text-gray-800"
                  }
                `}
                  >
                    {initials}
                  </div>
                  {/* Online status indicator */}
                  <div
                    className={`absolute -bottom-1 -right-1 w-3 h-3 border-2 rounded-full ${
                      theme === "dark"
                        ? "bg-[#cffa39] border-[#041221]"
                        : "bg-green-500 border-white"
                    }`}
                  ></div>
                </div>
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-300 group-hover:rotate-180 ${
                    theme === "dark" ? "text-[#cffa39]" : "text-gray-500"
                  }`}
                />
              </button>
            </PopoverTrigger>

            <PopoverContent
              align="end"
              className={`w-64 p-3 shadow-xl ${
                theme === "dark"
                  ? "bg-[#041221] border-[#cffa39]/20"
                  : "bg-white border-gray-200"
              }`}
              sideOffset={5}
            >
              {/* User Info Section */}
              <div
                className={`pb-3 mb-3 ${
                  theme === "dark"
                    ? "border-b border-[#cffa39]/20"
                    : "border-b border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  {/* Avatar in dropdown */}
                  <div
                    className={`
                  w-12 h-12 rounded-full flex items-center justify-center font-bold uppercase text-lg
                  ${
                    theme === "dark"
                      ? "bg-gradient-to-br from-[#041221] to-[#0a1a2f] border border-[#cffa39]/30 text-[#cffa39]"
                      : "bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 text-gray-800"
                  }
                `}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-semibold text-sm truncate ${
                        theme === "dark" ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {userName}
                    </p>
                    <p
                      className={`text-xs truncate ${
                        theme === "dark" ? "text-[#ffff]/70" : "text-gray-500"
                      }`}
                    >
                      {userEmail}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          theme === "dark"
                            ? "bg-[#cffa39]/20 text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {role || "User"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>


              {/* Enhanced Logout Button with Brand Colors */}
              <div
                className={`pt-5 ${
                  theme === "dark"
                    ? " border-[#cffa39]/20"
                    : "border-gray-200"
                }`}
              >
                <button
                  onClick={logout}
                  onMouseEnter={() => setIsLogoutHovered(true)}
                  onMouseLeave={() => setIsLogoutHovered(false)}
                  className="group relative w-full overflow-hidden rounded-xl transition-all duration-300"
                >
                  {/* Background glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#cffa39]/0 via-[#cffa39]/10 to-[#cffa39]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Main button container */}
                  <div
                    className={`
                  relative flex items-center justify-center gap-3
                  px-4 py-3
                  rounded-xl
                  transition-all duration-300
                  ${
                    theme === "dark"
                      ? "bg-gradient-to-br from-[#0a1a2f] to-[#041221] border border-[#cffa39]/30"
                      : "bg-gradient-to-br from-red-50 to-white border border-red-200"
                  }
                  ${isLogoutHovered ? "scale-[1.02] shadow-lg" : "shadow-md"}
                  ${
                    theme === "dark"
                      ? "group-hover:border-[#cffa39]/50 group-hover:shadow-[0_0_20px_rgba(207,250,57,0.3)]"
                      : "group-hover:border-red-300"
                  }
                  overflow-hidden
                `}
                  >
                    {/* Neon scan line effect */}
                    <div className="absolute top-0 left-0 right-0 h-[1px]">
                      <div
                        className={`
                      w-full h-full bg-gradient-to-r from-transparent via-[#cffa39] to-transparent
                      transition-all duration-1000
                      ${isLogoutHovered ? "translate-x-0 opacity-70" : "-translate-x-full opacity-0"}
                    `}
                      />
                    </div>

                    {/* Icon */}
                    <div className="relative">
                      <Power
                        size={16}
                        className={`
                        transition-all duration-300
                        ${theme === "dark" ? "text-[#cffa39]" : "text-red-600"}
                        ${isLogoutHovered ? "rotate-90 scale-110" : ""}
                      `}
                      />
                      {/* Pulsing glow behind icon */}
                      <div
                        className={`
                      absolute inset-0 rounded-full blur-sm
                      transition-opacity duration-300
                      ${theme === "dark" ? "bg-[#cffa39]/30" : "bg-red-200"}
                      ${isLogoutHovered ? "opacity-100" : "opacity-0"}
                    `}
                      />
                    </div>

                    {/* Text */}
                    <span
                      className={`
                    relative text-sm font-semibold tracking-wide transition-all duration-300
                    ${theme === "dark" ? "text-[#cffa39]" : "text-red-700"}
                    ${isLogoutHovered ? "tracking-widest" : ""}
                  `}
                    >
                      LOGOUT
                    </span>

                    {/* Shine effect overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  </div>
                </button>

                {/* Hover label */}
                <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                  <span
                    className={`text-[10px] ${
                      theme === "dark" ? "text-[#cffa39]/50" : "text-gray-500"
                    } font-mono`}
                  >
                    TERMINATE SESSION
                  </span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
